import _ from 'lodash';
import moment from 'moment';

export function mergeUpdateFromServer(itemToUpdate, update) {
  const mergedObject = {};

  // Since we load nested data onto pageData objects on initial page load, and then receive flat objects from server on
  // socket updates, we want to keep the additional loaded data when we update an item in the store.
  //
  // We recursively walk through the object being updated and:
  // 1. If the data in store and data returned from server both have nested objects loaded, recurse
  // 2. Otherwise if the update has nested loaded data, keep that data
  // 3. Otherwise if the key in the store has nested loaded data, keep that data
  // 4. Otherwise if the key in the update has a value, keep that data
  // 5. Otherwise, keep the old value
  _.forEach(itemToUpdate, (originalValue, key) => {
    if (_.isObjectLike(originalValue) && _.isObjectLike(update[key])) {
      // if itemToUpdate has object and update has object, recurse
      mergedObject[key] = mergeUpdateFromServer(originalValue, update[key]);
    } else if (_.isObjectLike(update[key])) {
      // if update has object & itemToUpdate doesn't, this key was updated on the server,
      // so we keep the new data
      mergedObject[key] = update[key];
    } else if (_.isObjectLike(originalValue)) {
      // if itemToUpdate has object & update doesn't, this key was loaded on the original
      // load but was not returned by the server (and therefore not changed), so keep the old data
      mergedObject[key] = originalValue;
    } else if (update[key]) {
      // If neither has object and there is new data at this key, keep the new data
      mergedObject[key] = update[key];
    } else {
      // If neither has object and there is no new data at this key, keep the old data
      mergedObject[key] = originalValue;
    }
  });

  return mergedObject;
}

export function mergeSocketObject(item = {}, oldItem, merge = [], options = {}) {
  if (!oldItem) return item;

  _.forEach(merge, keyString => {
    const keys = _.split(keyString, '.');
    const key = _.head(keys);

    item[key] = mergeSocketArray(item[key], oldItem[key], _.defaults({ nestedMerge: _.tail(keys) }, options));
  });

  return _.defaults({}, item, oldItem);
}

export function mergeSocketArray(newItems = [], oldItems = [], options = {}) {
  const { mergeIfLocked, nestedMerge, keepItemsFilters } = options;

  // Figure out which prop we are using for id
  const id = options.id || (_.get(newItems, '[0].uuid') ? 'uuid' : 'id');
  const map = _.keyBy(oldItems, id);

  let merged = _.map(newItems, item => {
    const oldItem = map[item[id]];

    if (!oldItem) return item;

    if (oldItem.forceMerge) {
      item.updatedAt = moment(oldItem.updatedAt).add(1, 'second');
      oldItem.locked = false;
      oldItem.forceMerge = false;
    }

    if (oldItem.updatedAt && moment(item.updatedAt).isSameOrBefore(oldItem.updatedAt)) return oldItem;
    
    if (nestedMerge) {
      _.forEach(nestedMerge, keyString => {
        const keys = _.split(keyString, '.');
        const key = _.head(keys);

        item[key] = mergeSocketArray(item[key], oldItem[key], { nestedMerge: _.tail(keys), keepItemsFilters });
      });
    }

    if (oldItem.id && oldItem.locked && moment().isBefore(oldItem.locked)) {
      const itemsToMerge = _.concat([], mergeIfLocked, _.map(nestedMerge, key => _.head(_.split(key, '.'))));

      if (itemsToMerge.length) return _.defaults({}, _.pick(item, itemsToMerge), oldItem, item);

      return _.defaults({}, oldItem, item);
    }

    // Merge down to keep presentation data
    return _.defaults({}, item, oldItem);
  });

  if (keepItemsFilters) {
    let additions = [];

    _.forEach(keepItemsFilters, filter => {
      const extraItems = _(oldItems)
        .differenceBy(newItems, id)
        .differenceBy(additions, id)
        .filter(filter)
        .value();

      additions = _.concat(additions, extraItems);
    });

    merged = _.concat(merged, additions);
  }

  return merged;
}
